<template>
  <div class="accounting-doc-grid">
    <div class="grid-icon-btn">
      <i class="el-icon-circle-plus" @click="addItem" />
      <i class="el-icon-remove" @click="removeItem" />
    </div>
    <cargo-ag-grid
      no="grid1"
      :grid-options="gridOptions"
      :column-defs="columnDefs"
      :row-data="rowData"
      :show-side-bar="false"
      :auto-fit-content="false"
      class="accounting-document-grid"
      style="width:100%;min-height:325px"
      @gridReady="params=>{gridApi=params.api}"
    />
    <cargo-ag-grid
      no="grid2"
      :grid-options="footerGridOptions"
      :column-defs="footerColumnDefs"
      :row-data="footerData"
      :show-side-bar="false"
      :auto-fit-content="false"
      class=" accounting-document-grid accounting-document-grid-footer"
      style="width:100%;"
    />
  </div>
</template>

<script>
import AccountingDocumentSummaryEditor from './AccountingDocumentSummaryEditor'
import AccountingDocumentAmountHeader from './AccountingDocumentAmountHeader'
import AccountingDocumentAmountRenderer from './AccountingDocumentAmountRenderer'
import AccountingDocumentAmountEditor from './AccountingDocumentAmountEditor'
import AccountingDocumentSummaryRenderer from './AccountingDocumentSummaryRenderer'
import CurrencyEditor from './CurrencyEditor'
import QuantityEditor from './QuantityEditor'
import * as accountTitleApi from '@/api/financeManage/accountTitle'
import Nzh from 'nzh/cn'
import EventBus from '@/utils/eventBus'
import AccountTitleEditor from '@/views/cargo/v0/FinanceManage/AccountingDocument/components/AccountTitleEditor'
import { cache } from '@/utils/cacheManager'
import StrUtils from '@/utils/StrUtils'
import { basicCacheKeys } from '@/utils/cacheConfig'
import { getSpecificBasicData } from '@/utils/basicDataCache'
import ColumnUtils from '@/utils/ColumnUtils'
import { TYPE_NAMES } from '@/utils/class/AdvanceSelect'
import * as NumberUtils from '@/utils/NumberUtils'

export default {
  name: 'AccountingDocumentGrid',
  props: {
    type: {
      type: String,
      default: 'add'
    },
    rowData: {
      type: Array,
      default: null
    },
    accountSet: { type: Object, default: null },
    accountTitle: { type: Object, default: null },
    fiscalPeriod: { type: Object, default: null }
  },
  data() {
    return {
      footerData: [{
        summary: '',
        debitAmount: 0,
        crebitAmount: 0
      }],
      selectedNodes: [],
      pageIndex: 0,
      accountingDocument: null,
      accountTitleCollection: [],
      salesList: [],
      showQuantity: false,
      showCurrency: false,
      quantityColWidth: 150,
      currencyColWidth: 150,
      totalColWidth: 525,
      summaryDisplayMode: false,
      selectedNode: {},
      gridApi: null
    }
  },
  computed: {
    gridOptions() {
      return {
        alignedGrids: [],
        suppressContextMenu: true,
        suppressHorizontalScroll: true,
        rowSelection: 'single',
        suppressRowSelection: false,
        suppressRowClickSelection: false,
        singleClickEdit: true,
        rowHeight: 64,
        defaultColDef: {
          suppressMenu: true,
          suppressMovable: true,
          headerClass: 'text-center'
        },
        frameworkComponents: {
          AccountingDocumentSummaryEditor,
          AccountingDocumentAmountHeader,
          AccountingDocumentSummaryRenderer,
          AccountingDocumentAmountEditor,
          AccountingDocumentAmountRenderer,
          AccountTitleEditor,
          QuantityEditor,
          CurrencyEditor
        },
        headerHeight: 64,
        onSelectionChanged: this.onSelectionChanged,
        onCellMouseOver: this.onCellMouseOver
      }
    },
    footerGridOptions() {
      return {
        alignedGrids: [],
        headerHeight: 0,
        suppressContextMenu: true,
        suppressRowSelection: true,
        suppressRowClickSelection: true,
        rowHeight: 30,
        defaultColDef: {
          suppressMenu: true,
          suppressMovable: true,
          headerClass: 'text-center'
        },
        frameworkComponents: {
          AccountingDocumentAmountRenderer,
          AccountingDocumentSummaryRenderer
        }
      }
    },
    columnDefs() {
      return [
        {
          headerName: this.$t('CargoMsg.Cargo_Abstract'),
          field: 'summary',
          editable: true,
          cellEditor: 'AccountingDocumentSummaryEditor',
          cellRenderer: 'AccountingDocumentSummaryRenderer',
          cellEditorParams: params => {
            return {
              value: StrUtils.isEmpty(params.data.summary) ? this.defalutSummary() : params.data.summary
            }
          },
          width: 225,
          sortable: false
        }, {
          headerName: this.$t('CargoMsg.Cargo_TitleOfAccount'),
          field: 'accountTitleColumn',
          width: 300,
          sortable: false,
          editable: true,
          cellEditor: 'AccountTitleEditor',
          cellEditorParams: params => {
            return {
              accountTitles: this.accountTitleCollection,
              accountingEntry: params.data,
              value: params.data.accountTitle,
              salesList: this.salesList
            }
          },
          valueFormatter: params => {
            return this.type === 'add' ? params.value : this.accountTitleColumn(params.data)
          },
          onCellValueChanged: (params) => {
            this.changeHandler(params, params.data)
          }
        }, {
          headerName: this.$t('CargoMsg.Cargo_AmountNumber'),
          field: 'quantityAndUnitColumn',
          width: this.quantityColWidth,
          sortable: false,
          hide: !this.showQuantity,
          editable: params => {
            const accountTitle = this.accountTitleCollection.find(accountTitle => accountTitle.accountTitle === params.data.accountTitle)
            return accountTitle ? accountTitle.accountingQuantity : false
          },
          cellEditor: 'QuantityEditor',
          cellEditorParams: params => {
            return {
              accountSet: this.accountSet,
              accountTitle: params.data.accountTitle,
              accountingEntry: params.data,
              fiscalPeriod: this.fiscalPeriod,
              accountTitles: this.accountTitleCollection,
              value: params.data.quantity
            }
          },
          // valueFormatter: params => {
          //   return params.value
          // }
          cellRenderer: params => {
            if (params.data.quantity && params.data.price) {
              return this.$t('FinanceManage.Quantity') + params.data.quantity + '<br/>' + this.$t('FinanceManage.UnitPrice') + params.data.price
            }
            return ''
          }
        }, {
          headerName: this.$t('CargoMsg.Cargo_Currency'),
          field: 'currencyColumn',
          width: this.currencyColWidth,
          sortable: false,
          hide: !this.showCurrency,
          editable: params => {
            const accountTitle = this.accountTitleCollection.find(accountTitle => accountTitle.accountTitle === params.data.accountTitle)
            return accountTitle ? accountTitle.accountingCurrency : false
          },
          cellEditor: 'CurrencyEditor',
          cellEditorParams: params => {
            return {
              accountSet: this.accountSet,
              accountTitle: params.data.accountTitle,
              accountingEntry: params.data,
              fiscalPeriod: this.fiscalPeriod,
              accountTitles: this.accountTitleCollection,
              value: params.data.sourceCurrency
            }
          },
          cellRenderer: params => {
            if (params.data.sourceCurrency) {
              return this.$t('FinanceManage.ForeignCurrency') + params.data.sourceAmount + '<br/>' + params.data.sourceCurrency + ':' + params.data.exchangeRate
            }
            return ''
          },
          onCellValueChanged: (params) => {
            this.calculateAmount()
          }
        }, {
          headerName: this.$t('CargoMsg.Cargo_DebitAmount2'),
          field: 'debitAmount',
          editable: true,
          headerComponent: 'AccountingDocumentAmountHeader',
          cellEditor: 'AccountingDocumentAmountEditor',
          cellRenderer: 'AccountingDocumentAmountRenderer',
          cellEditorParams: params => {
            return {
              value: params.data.debitAmount,
              field: 'debitAmount',
              accountingEntry: params.data,
              accountingEntrys: this.rowData
            }
          },
          onCellValueChanged: (params) => {
            const { newValue, oldValue, node } = params
            if (newValue && newValue !== oldValue) {
              node.setDataValue('crebitAmount', 0)
            } else if (params.data.crebitAmount) {
              node.setDataValue('crebitAmount', params.data.crebitAmount)
              params.api.setFocusedCell(this.selectedNode.rowIndex, 'crebitAmount')
            }
            this.calculateAmount()
          },
          width: 298,
          sortable: false
        }, {
          headerName: this.$t('CargoMsg.Cargo_CreditAmount2'),
          field: 'crebitAmount',
          editable: true,
          headerComponent: 'AccountingDocumentAmountHeader',
          cellEditor: 'AccountingDocumentAmountEditor',
          cellRenderer: 'AccountingDocumentAmountRenderer',
          cellEditorParams: params => {
            return {
              value: params.data.crebitAmount,
              field: 'crebitAmount',
              accountingEntry: params.data,
              accountingEntrys: this.rowData
            }
          },
          onCellValueChanged: (params) => {
            const { newValue, oldValue, node } = params
            if (newValue && newValue !== oldValue) {
              node.setDataValue('debitAmount', 0)
            } else if (params.data.debitAmount) {
              node.setDataValue('debitAmount', params.data.debitAmount)
              params.api.setFocusedCell(this.selectedNode.rowIndex, 'debitAmount')
            }
            this.calculateAmount()
          },
          width: 298,
          sortable: false
        }
      ]
    },
    footerColumnDefs() {
      return [
        {
          headerName: '',
          field: 'totalColumn',
          width: this.totalColWidth,
          sortable: false,
          valueFormatter: (params) => {
            return `${this.$t('CargoMsg.Cargo_AmountToP')}${Nzh.toMoney(Number(params.data.debitAmount), { outSymbol: false })}`
          }
        }, {
          headerName: this.$t('CargoMsg.Cargo_DebitAmount2'),
          field: 'debitAmount',
          editable: false,
          cellRenderer: 'AccountingDocumentAmountRenderer',
          width: 298,
          sortable: false
        }, {
          headerName: this.$t('CargoMsg.Cargo_CreditAmount2'),
          field: 'crebitAmount',
          editable: false,
          cellRenderer: 'AccountingDocumentAmountRenderer',
          width: 298,
          sortable: false
        }
      ]
    },
    hasSelected() {
      return this.selectedNodes.length > 0
    },
    showAllAccountTitleName() {
      return Number(cache.config.get('ShowAllAccountTitleName')) === 1
    },
    selectedDebitAmount() {
      return this.selectedNode && this.selectedNode.data ? this.selectedNode.data.debitAmount : 0
    },
    selectedCrebitAmount() {
      return this.selectedNode && this.selectedNode.data ? this.selectedNode.data.crebitAmount : 0
    }
  },
  watch: {
    async accountSet(newVal) {
      if (newVal) {
        this.accountTitleCollection = this.accountSet.accountTitle // await accountTitleApi.query({ accountingSetKey: this.accountSet.uuid }).then(resp => resp.data.list)
        this.salesList = await getSpecificBasicData(basicCacheKeys.basic_data_User)
        this.summaryDisplayMode = Number(cache.config.get('summaryDisplayMode')) === 1
      }
    },
    rowData: {
      handler(newVal, oldVal) {
        if (newVal) {
          this.calculateAmount()
          if (['edit', 'copy'].includes(this.type) && this.rowData && this.rowData.length > 0) {
            for (const accountingEntry of this.rowData) {
              if (accountingEntry.accountTitle) {
                const accountTitle = this.accountTitleCollection.find(title => title.accountTitle === accountingEntry.accountTitle)
                // 数量
                if (!this.showQuantity) {
                  this.showQuantity = !!accountTitle.accountingQuantity
                }
                // 币种
                if (!this.showCurrency) {
                  this.showCurrency = !!accountTitle.accountingCurrency
                }
              }
              if (this.showQuantity && this.showCurrency) {
                return
              }
            }
            if (this.showQuantity) {
              this.gridOptions.columnApi.setColumnVisible('quantityAndUnitColumn', this.showQuantity)
            }
            if (this.showCurrency) {
              this.gridOptions.columnApi.setColumnVisible('currencyColumn', this.showCurrency)
            }
            if (this.showQuantity || this.showCurrency) {
              this.changeWidth()
            }
          }
        }
      },
      deep: true
    },
    selectedDebitAmount() {
      if (this.gridOptions.api) {
        const cell = this.gridOptions.api.getFocusedCell()
        // 被联动修改时，刷新cell
        if (cell && cell.column.colDef.field !== 'debitAmount') {
          this.gridOptions.api.refreshCells({ force: true })
        }
      }
    },
    selectedCrebitAmount() {
      if (this.gridOptions.api) {
        const cell = this.gridOptions.api.getFocusedCell()
        // 被联动修改时，刷新cell
        if (cell && cell.column.colDef.field !== 'crebitAmount') {
          this.gridOptions.api.refreshCells({ force: true })
        }
      }
    }
  },
  created() {
    this.gridOptions.alignedGrids.push(this.footerGridOptions)
    this.footerGridOptions.alignedGrids.push(this.gridOptions)
    if (this.type === 'add') {
      for (let i = 0; i < 4; i++) {
        this.addItem()
      }
    }
    EventBus.$on('accountingDocumentGridAddItem', this.addItem)
    EventBus.$on('accountingDocumentGridDeleteItem', this.removeItem)
    EventBus.$on('accountingDocumentGridExchange', this.exchange)
    EventBus.$on('accountingDocumentGridCalculateBalance', this.calculateBalance)
  },
  beforeDestroy() {
    EventBus.$off()
  },
  methods: {
    addItem() {
      const rowItem = {
        index: Math.floor(Math.random() * 1000),
        summary: '',
        accountTitle: '',
        debitAmount: 0,
        crebitAmount: 0,
        amount: 0,
        exchangeRate: 0,
        sourceAmount: 0,
        sourceCurrency: ''
      }
      if (this.selectedNodes.length > 0) {
        const { rowIndex } = this.selectedNodes[0]
        if (rowIndex < this.rowData.length - 1) {
          this.rowData.splice(rowIndex + 1, 0, rowItem)
        } else {
          this.rowData.push(rowItem)
        }
        setTimeout(() => {
          this.gridOptions.api.setFocusedCell(rowIndex + 1, this.gridOptions.api.columnController.getAllDisplayedColumns()[0])
        })
      } else {
        this.rowData.push(rowItem)
        setTimeout(() => {
          this.gridOptions.api.setFocusedCell(this.rowData.length - 1, this.gridOptions.api.columnController.getAllDisplayedColumns()[0])
        })
      }
    },
    removeItem() {
      if (this.selectedNodes.length) {
        const { rowIndex } = this.selectedNodes[0]
        this.rowData.splice(rowIndex, 1)
      } else {
        this.rowData.splice(this.rowData.length - 1, 1)
      }
      this.calculateAmount()
    },
    exchange() {
      const node = this.selectedNodes[0]
      const { debitAmount, crebitAmount } = node.data
      node.setDataValue('debitAmount', crebitAmount)
      node.setDataValue('crebitAmount', debitAmount)
      this.calculateAmount()
    },
    calculateBalance() {
      console.log('计算余额')
    },
    calculateAmount() {
      const types = ['debitAmount', 'crebitAmount']
      types.forEach(type => {
        this.footerData[0][type] = this.rowData.reduce((total, item) => {
          total += isNaN(item[type]) ? 0 : item[type]
          total = NumberUtils.roundHalfUp4(total)
          return total
        }, 0)
      })
      if (this.footerGridOptions.api) {
        this.footerGridOptions.api.setRowData(this.footerData)
      }
    },
    onSelectionChanged(params) {
      const { api } = params
      this.selectedNodes = api.getSelectedNodes()
      console.log(params)
      EventBus.$emit('accountingDocumentGridItemSelected', this.selectedNodes.length > 0)
    },
    validate(accountingDocument) {
      // 录入借贷不平！
      const footerRow = this.footerData[0]
      if (Math.abs(footerRow.crebitAmount - footerRow.debitAmount) > 0.0001) {
        this.$message.warning('录入借贷不平！')
        return false
      }
      // 业务生成的凭证借贷方金额合不能改变
      if (accountingDocument.businessDataType !== 999) {
        if (accountingDocument.debitTotal !== footerRow.debitTotal ||
          accountingDocument.creditTotal !== footerRow.creditTotal) {
          this.$message.warning('  此凭证是业务凭证\n请至“生成凭证”模块编辑')
          return false
        }
      }

      // 请录入借贷方金额！

      // 请录入至少二条有效分录！
      if (this.validateItemAxuiliary()) {
        return false
      }

      for (const item of this.rowData) {
        if (item && this.validateAccountingEntry(item)) {
          const ae = this.retrieveAccountingEntry(item)
          if (ae.summary.trim().length === 0) {
            this.$message.warning(this.$t('CargoMsg.Cargo_IndexCanTBeEmpty'))
            return false
          }
          if (item.accountTitle.accountingCurrency) {
            if (item.sourceCurrency.trim().length === 0) {
              this.$message.warning(this.$t('FinanceManage.ForeignCurrencyCannotBeEmpty'))
              return false
            }
            if (item.exchangeRate.trim().length === 0) {
              this.$message.warning(this.$t('CargoMsg.Cargo_ExchangeRateCanTBeEmpty'))
              return false
            }
          }
          if (!item.sourceCurrency) {
            this.$message.warning(this.$t('CargoMsg.Cargo_SOURCE_CURRENCY_NOT_NULL'))
            return false
          }
          if (!item.sourceAmount) {
            this.$message.warning(this.$t('CargoMsg.Cargo_SOURCE_AMOUNT_NOT_NULL'))
            return false
          }
        }
      }
      return true
    },
    validateItemAxuiliary(showMessage = true) {
      for (const item of this.rowData) {
        if (item && this.validateAccountingEntry(item)) {
          const ae = this.retrieveAccountingEntry(item)
          if (item.accountTitle && item.accountTitle.auxiliary) {
            if (item.accountTitle.auxiliaryCustomer) {
              if (!ae.accountClient) {
                if (showMessage) {
                  this.$message.warning(this.$t('FinanceManage.ClientItemCannotBeEmpty'))
                }
                return item
              }
            }
            if (item.accountTitle.auxiliarySupplier) {
              if (!ae.accountClient) {
                if (showMessage) {
                  this.$message.warning(this.$t('FinanceManage.SupplierItemCannotBeEmpty'))
                }
                return item
              }
            }
            if (item.accountTitleCB.selectedItem.auxiliarySales) {
              if (!ae.sales) {
                if (showMessage) {
                  this.$message.warning(this.$t('FinanceManage.StaffItemCannotBeEmpty'))
                }
                return item
              }
            }
            if (item.accountTitle.auxiliaryProjectNumber) {
              if (ae.projectNumber.trim().length === 0) {
                if (showMessage) {
                  this.$message.warning(this.$t('FinanceManage.ProjectItemCannotBeEmpty'))
                }
                return item
              }
            }
            if (item.accountTitle.auxiliaryDepartment) {
              if (!ae.department) {
                if (showMessage) {
                  this.$message.warning(this.$t('FinanceManage.DepartmentItemCannotBeEmpty'))
                }
                return item
              }
            }
          }
        }
      }
      return null
    },
    retrieveAccountingEntry(accountingEntry) {
      const accountTitle = this.accountTitleCollection.find(title => title.accountTitle === accountingEntry.accountTitle)
      if (!accountTitle) {
        return accountingEntry
      }
      // const accountTitleStr = accountingEntry.accountTitle
      // accountingEntry.accountTitle = accountingEntry.accountTitle ? accountingEntry.accountTitle.split(' ')[0] : null
      if (accountingEntry.accountTitle) {
        if (this.showAllAccountTitleName && this.accountTitleMap) {
          accountingEntry.accountTitleName = this.accountTitleMap[accountingEntry.accountTitle]
        } else {
          // accountingEntry.accountTitleName = accountTitleStr.split(' ')[1]
        }
      } else {
        accountingEntry.accountTitleName = null
      }
      accountingEntry.amount = accountingEntry.debitAmount || accountingEntry.crebitAmount
      accountingEntry.debitCrebit = !!accountingEntry.debitAmount
      // 数量
      if (accountTitle && accountTitle.accountingQuantity) {
        accountingEntry.quantity = accountingEntry.quantityAndUnit[0] ? (isNaN(Number(accountingEntry.quantityAndUnit[0])) ? 0 : Number(accountingEntry.quantityAndUnit[0])) : 0
        accountingEntry.unit = accountingEntry.quantityAndUnit[1]
      } else {
        accountingEntry.quantity = 0
        accountingEntry.unit = null
      }

      // if (this.accountTitle && this.accountTitle.auxiliary) {
      //   if (this.accountTitle.auxiliaryCustomer) {
      //     accountingEntry.accountClient = this.accountTitle.auxiliaryCustomer
      //   }
      //   if (this.accountTitle.auxiliarySupplier) {
      //     accountingEntry.accountClient = this.accountTitle.auxiliarySupplier
      //   }
      //   if (this.accountTitle.auxiliarySales) {
      //     accountingEntry.sales = this.accountTitle.auxiliarySales
      //   }
      //   if (this.accountTitle.auxiliaryProjectNumber) {
      //     accountingEntry.projectNumber = this.accountTitle.auxiliaryProject
      //   }
      //   if (this.accountTitle.auxiliaryDepartment) {
      //     accountingEntry.department = this.accountTitle.auxiliaryDepartment
      //   }
      // } else {
      //   // 部门
      //   accountingEntry.department = 0
      //   // 职员
      //   accountingEntry.sales = 0
      //   // 客户/供应商
      //   accountingEntry.accountClient = 0
      //   // 项目
      //   accountingEntry.projectNumber = ''
      // }
      // if (this.accountTitle && this.accountTitle.accountingCurrency) {
      //   accountingEntry.sourceCurrency = accountingEntry.currency[0] ? accountingEntry.currency[0] : 'RMB'
      //   accountingEntry.sourceAmount = Number(accountingEntry.currency[1].trim())
      //   accountingEntry.exchangeRate = Number(accountingEntry.currency[2])
      // } else {
      //   accountingEntry.sourceCurrency = 'RMB'
      //   accountingEntry.sourceAmount = accountingEntry.amount
      //   accountingEntry.exchangeRate = 1
      // }
      // 非外币核算时，原币币种必须是RMB,且金额和原币金额要一致。
      if (!accountTitle.accountingCurrency) {
        accountingEntry.sourceCurrency = this.accountSet.localCurrency
        accountingEntry.sourceAmount = accountingEntry.amount
        accountingEntry.exchangeRate = 1
      }
      return accountingEntry
    },
    retrieveEntry() {
      const accountingDocument = this.accountingDocument
      if (!accountingDocument.accountingEntry) {
        accountingDocument.accountingEntry = []
      }
      const value = accountingDocument.accountingEntry.length
      const allNum = this.getAllPageSize(accountingDocument.accountingEntry.length)
      const nowPage = this.getPageNewCount()
      if (allNum > 1 && (value + nowPage) > this.rowData.length) {
        const pageSize = this.accountSet.voucherLines
        // 仅移除当前页数据
        const num = value ? (value > pageSize ? value : pageSize) : pageSize
        const nPage = pageSize * this.pageIndex
        const lPage = pageSize * (this.pageIndex - 1)
        if (num > 0) {
          // 已经移除的数量
          let d = 0
          for (let i; i <= num - 1; i++) {
            if (i < nPage && i >= lPage) {
              accountingDocument.accountingEntry.splice(i - d, 1)
              d++
            }
          }
        }
      } else {
        accountingDocument.accountingEntry = []
      }
      // 当前页新数据添加
      let serialNumber = 0
      for (const item of this.rowData) {
        if (item && this.validateAccountingEntry(item)) {
          const accountingEntry = this.retrieveAccountingEntry(item)
          accountingEntry.serialNumber = serialNumber
          accountingDocument.accountingEntry.push(accountingEntry)
          serialNumber += 1
          if (item.accountingEntry && !accountingDocument.accountingEntry.includes(item.accountingEntry)) {
            accountingDocument.accountingEntry.push(item.accountingEntry)
          }
        }
      }
    },
    retrieveDebitCreditTotal() {
      const footerRow = this.footerData[0]
      this.accountingDocument.debitTotal = footerRow.debitAmount
      this.accountingDocument.creditTotal = footerRow.crebitAmount
    },
    /**
     *  获取总页数
     * @return
     */
    getAllPageSize(pageSum) {
      // 账套维护中每页数量
      const size = this.accountSet.voucherLines
      let all = pageSum % size === 0 ? (pageSum / size) : (Math.floor(pageSum / size) + 1)
      if (all === 0) {
        all += 1
      }
      return all
    },
    /**
     * 获取当前页面新增数据量
     * @return
     */
    getPageNewCount() {
      let num = 0
      for (const item of this.rowData) {
        const ae = item.accountingEntry
        if (ae != null && !ae.uuid) {
          num += 1
        }
      }
      return num
    },
    /**
     *  本位币金额不等于原币*汇率,是否可以保存
     */
    validateLocalAmount() {
      for (const item of this.rowData) {
        if (item && this.validateAccountingEntry(item)) {
          if (item.accountTitle && item.accountTitle.accountingCurrency) {
            const rateNum = Number(item.exchangeRate.trim())
            const sourceCurrencyNum = Number(item.sourceCurrency.trim())
            const amountNum = item.debitAmount || item.crebitAmount
            if (amountNum !== Number((rateNum * sourceCurrencyNum).toFixed(2))) {
              return true
            }
          }
        }
      }
      return false
    },
    validateAccountingEntry(accountingEntry) {
      if (!accountingEntry) {
        return false
      }
      if (accountingEntry.uuid) {
        return true
      }
      if ((accountingEntry.summary && accountingEntry.summary.trim()) || accountingEntry.accountTitle || this.retrieveAmount(accountingEntry) !== 0) {
        return true
      }
      return false
    },
    retrieveAmount(accountingEntry) {
      if (accountingEntry.debitAmount && Number(accountingEntry.debitAmount) !== 0) {
        return Number(accountingEntry.debitAmount)
      }
      if (accountingEntry.crebitAmount && Number(accountingEntry.crebitAmount) !== 0) {
        return Number(accountingEntry.crebitAmount)
      }
      return 0
    },
    changeHandler(params, accountingEntry) {
      if (params.newValue === params.oldValue) {
        return
      }
      if (accountingEntry.accountTitle) {
        const accountTitle = this.accountTitleCollection.find(title => title.accountTitle === accountingEntry.accountTitle)
        if (accountTitle.accountingCurrency) { // 外币核算,找对应的币种
          this.currencyChange(accountingEntry)
        } else { // 非外币核算，默认原币币种是RMB,汇率是1
          accountingEntry.sourceAmount = 0
          accountingEntry.sourceCurrency = 'RMB'
          accountingEntry.exchangeRate = 1.0
        }

        // 数量
        let flag = accountTitle.accountingQuantity
        if (flag) {
          if (!this.showQuantity) {
            this.showQuantity = true
            this.gridOptions.columnApi.setColumnVisible('quantityAndUnitColumn', this.showQuantity)
          }
        }
        // 币种
        flag = accountTitle.accountingCurrency // 是否外币核算
        if (flag) {
          if (!this.showCurrency) {
            this.showCurrency = true
            this.gridOptions.columnApi.setColumnVisible('currencyColumn', this.showCurrency)
          }
        }
        if (this.showQuantity || this.showCurrency) {
          this.changeWidth()
        }
        if (this.showAllAccountTitleName) {
          if (this.accountTitleMap == null) {
            this.accountTitleMap = {}
          }
          accountTitleApi.getAccountTitleName({ accountingSetKey: this.accountSet.uuid, accountTitle: accountingEntry.accountTitle }).then(resp => {
            this.accountTitleMap[accountingEntry.accountTitle] = resp.data
          })
        }
        if (this.gridApi) {
          this.gridApi.refreshCells({ force: true })
        }
      }
    },
    changeTotalColWidth() {
      let width = 525
      if (this.showQuantity) width += this.quantityColWidth
      if (this.showCurrency) width += this.currencyColWidth
      this.totalColWidth = width
      this.footerGridOptions.columnApi.setColumnWidth('totalColumn', this.totalColWidth)
    },
    changeWidth() {
      this.$emit('changeWidth')
      this.changeTotalColWidth()
    },
    accountTitleColumn(accountingEntry) {
      if (accountingEntry) {
        const accountTitle = this.accountTitleCollection.find(title => title.accountTitle === accountingEntry.accountTitle)
        if (!accountTitle) return ''
        let msg = accountTitle.accountTitle + ' ' + accountTitle.accountTitleName
        const auxiliaryStr = this.fillVoFromAuxiliary(accountingEntry, accountTitle)
        if (auxiliaryStr) {
          msg += ' \n' + auxiliaryStr
        }
        return msg
      }
      return ''
    },
    fillVoFromAuxiliary(accountingEntry, accountTitle) {
      if (accountTitle) {
        let auxiliaryStr = ''
        if (accountTitle && accountTitle.auxiliary) {
          if (accountTitle.auxiliaryCustomer) {
            if (auxiliaryStr) {
              auxiliaryStr += '/'
            }
            if (accountingEntry.accountClient) {
              const customer = ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_Company, 'cmpIdx', accountingEntry.accountClient, 'chineseName')
              if (!StrUtils.isEmpty(customer)) auxiliaryStr += customer
            }
          }
          if (accountTitle.auxiliarySupplier) {
            if (auxiliaryStr) {
              auxiliaryStr += '/'
            }
            if (accountingEntry.accountClient) {
              const supplier = ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_Company, 'cmpIdx', accountingEntry.accountClient, 'chineseName')
              if (!StrUtils.isEmpty(supplier)) auxiliaryStr += supplier
            }
          }
          if (accountTitle.auxiliaryDepartment) {
            if (auxiliaryStr) {
              auxiliaryStr += '/'
            }
            if (accountingEntry.department) {
              const department = ColumnUtils.departmentLabelNewFunc(accountingEntry.department)
              if (!StrUtils.isEmpty(department)) auxiliaryStr += department
            }
          }
          if (accountTitle.auxiliarySales) {
            if (auxiliaryStr) {
              auxiliaryStr += '/'
            }
            if (accountingEntry.sales) {
              const sales = ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', accountingEntry.sales, 'chineseName')
              if (!StrUtils.isEmpty(sales)) auxiliaryStr += sales
            }
          }
          if (accountTitle.auxiliaryProjectNumber) {
            if (auxiliaryStr) {
              auxiliaryStr += '/'
            }
            if (accountingEntry.projectNumber) {
              auxiliaryStr += accountingEntry.projectNumber
            }
          }
          this.auxiliaryString = auxiliaryStr
          return auxiliaryStr
        }
      }
    },
    onCellMouseOver(event) {
      this.selectedNode = event.node
    },
    // 摘要
    defalutSummary() {
      if (this.selectedNode.rowIndex > 0 && StrUtils.isEmpty(this.selectedNode.data.summary)) {
        let previousRow = this.rowData[0]
        if (this.summaryDisplayMode && this.selectedNode.rowIndex > 1) previousRow = this.rowData[this.selectedNode.rowIndex - 1]
        if (!StrUtils.isEmpty(previousRow.summary)) {
          return previousRow.summary
        }
      }
      return ''
    },
    currencyChange(accountingEntry) {
      const rates = this.accountSet.rates
      if (!rates || rates.length === 0 || !accountingEntry.accountTitle) return
      const result = []
      let containRMB = false
      if (this.accountTitleCollection && this.accountTitleCollection.length > 0) {
        const accountTitle = this.accountTitleCollection.find(title => title.accountTitle === accountingEntry.accountTitle)
        if (accountTitle && accountTitle.sourceCurrency) {
          const currencyArray = accountTitle.sourceCurrency.split(',')
          for (const item of currencyArray) {
            if (item === this.accountSet.localCurrency) {
              containRMB = true
            }
            for (const rate of rates) {
              if (rate.rate > 0 && rate.sourceCurrency === item && this.fiscalPeriod && rate.year === this.fiscalPeriod.year && rate.month === this.fiscalPeriod.month) {
                result.push(rate)
              }
            }
          }
          if (containRMB) {
            accountingEntry.sourceCurrency = this.accountSet.localCurrency
            accountingEntry.sourceAmount = 0
            accountingEntry.exchangeRate = 1.0
          }
        }
      }
    }
  }
}
</script>

<style lang="scss">
.accounting-document-grid{
  .ag-cell-inline-editing {
    height: 100%!important;
  }
  .ag-header-cell{
    &.text-center {
      text-align: center;
      .ag-header-cell-text{
        display: inline;
        width: 100%;
        height: 100%;
        line-height: 64px;
      }
    }
    &::after {
      height: 48px;
    }
  }
  .ag-cell {
    border-right: 1px solid #d9dcde;
  }
  .ag-cell-inline-editing {
    height: 30px;
  }
  &-footer {
    height: 34px;
    .ag-theme-balham {
      min-height: 34px!important;
    }
  }
}
</style>
<style lang="scss" scoped>
.accounting-doc-grid{
  position: relative;
  .grid-icon-btn{
    height: 50px;
    position: absolute;
    top: 50%;
    left: -24px;
    z-index: 1;
    transform: translateY(-50%);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
    i{
      width: 24px;
      height: 24px;
      display: inline-block;
      font-size: 24px;
      cursor: pointer;
      &.el-icon-circle-plus{
        color: green;
      }
      &.el-icon-remove{
        color: red;
      }
    }
  }
  .accounting-document-grid::v-deep {
    .ag-center-cols-viewport {
      max-height: 100%;
    }
  }
  .accounting-document-grid-footer::v-deep {
    .ag-root.ag-unselectable.ag-layout-normal {
      height: fit-content;
      .ag-center-cols-clipper {
        height: unset !important;
        .ag-center-cols-viewport {
          max-height: unset;
        }
      }
    }
  }
}
</style>
